(() => {
  const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');

  const wait = (duration, signal) => new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Animation restarted', 'AbortError'));
      return;
    }
    const abort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Animation restarted', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      signal?.removeEventListener('abort', abort);
      resolve();
    }, duration);
    signal?.addEventListener('abort', abort, { once: true });
  });

  const revealExampleCard = async (trigger, image) => {
    const sources = (trigger.dataset.cardPool || '').split('|').filter(Boolean).slice(0, 3);
    if (!sources.length || !image) return '';
    const names = (trigger.dataset.namePool || '').split('|');
    const index = Math.floor(Math.random() * sources.length);
    const name = names[index] || trigger.dataset.productTitle || 'Topps Karte';
    trigger.dataset.cardSrc = sources[index];
    trigger.dataset.cardName = name;
    image.src = sources[index];
    image.alt = name;
    if (image.decode) await image.decode().catch(() => {});
    return name;
  };

  const setStatus = (root, message) => {
    const status = root.querySelector('[data-tsl-rip-status]');
    if (status) status.textContent = message;
  };

  const initializeScene = (root) => {
    const scene = root.querySelector('[data-tsl-scene]');
    const triggers = [...root.querySelectorAll('[data-tsl-rip-item]')];
    if (!scene || !triggers.length) return;

    const messages = JSON.parse(root.querySelector('[data-tsl-rip-messages]').textContent);
    const message = (key, productName, cardName = '') => messages[key]
      .replaceAll('{product}', () => productName)
      .replaceAll('{name}', () => cardName);

    let activeTrigger = null;
    let sequenceId = 0;

    const resetTrigger = (trigger) => {
      const productName = trigger.dataset.productTitle || 'Topps Pack';
      trigger.classList.remove('is-active', 'is-opening', 'is-flashing', 'is-revealed');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-label', message('open_label', productName));
      trigger.removeAttribute('aria-busy');
    };

    const closeSelection = () => {
      sequenceId += 1;
      triggers.forEach(resetTrigger);
      scene.classList.remove('has-selection');
      activeTrigger = null;
      setStatus(root, messages.ready);
    };

    const openTrigger = async (trigger) => {
      sequenceId += 1;
      const currentSequence = sequenceId;
      triggers.forEach(resetTrigger);
      activeTrigger = trigger;
      scene.classList.add('has-selection');
      trigger.classList.add('is-active', 'is-opening');
      trigger.setAttribute('aria-busy', 'true');
      trigger.setAttribute('aria-expanded', 'true');

      const productName = trigger.dataset.productTitle || 'Topps Pack';
      setStatus(root, message('opening', productName));
      const cardName = await revealExampleCard(trigger, trigger.querySelector('.tsl__hit-card img'));
      if (sequenceId !== currentSequence) return;

      if (!REDUCED_MOTION.matches) {
        await wait(520);
        if (sequenceId !== currentSequence) return;
        trigger.classList.add('is-flashing');
        await wait(220);
        if (sequenceId !== currentSequence) return;
      }

      trigger.classList.add('is-revealed');
      trigger.classList.remove('is-flashing');
      trigger.removeAttribute('aria-busy');
      const hasProductLink = Boolean(trigger.dataset.productUrl);
      trigger.setAttribute('aria-label', message(hasProductLink ? 'product_label' : 'open_label', productName));
      setStatus(root, hasProductLink ? message('revealed', productName, cardName) : `${cardName}. ${messages.ready}`);
    };

    triggers.forEach((trigger) => {
      trigger.addEventListener('click', () => {
        if (activeTrigger === trigger) {
          if (!trigger.classList.contains('is-revealed')) return;
          const productUrl = trigger.dataset.productUrl;
          if (productUrl) window.location.assign(productUrl);
          else openTrigger(trigger);
          return;
        }

        openTrigger(trigger);
      });
    });

    root.addEventListener('click', (event) => {
      if (!activeTrigger || event.target.closest('[data-tsl-rip-item]')) return;
      closeSelection();
    });

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !activeTrigger) return;
      closeSelection();
      scene.focus({ preventScroll: true });
    });
  };

  const initializeReveals = (root) => {
    const items = root.querySelectorAll('[data-tsl-reveal]');

    if (!('IntersectionObserver' in window) || REDUCED_MOTION.matches) {
      items.forEach((item) => item.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -34px' });

    items.forEach((item, index) => {
      item.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
      observer.observe(item);
    });
  };

  const initializeSleeveStation = (root) => {
    const station = root.querySelector('[data-tsl-sleeve]');
    if (!station) return;
    const pulls = [...station.querySelectorAll('[data-tsl-pull]')];
    const soft = station.querySelector('[data-tsl-soft-stage]');
    const loader = station.querySelector('[data-tsl-loader-stage]');
    const cards = station.querySelectorAll('[data-tsl-protect-card]');
    const status = station.querySelector('[data-tsl-sleeve-status]');
    const messages = JSON.parse(station.querySelector('[data-tsl-protection-messages]').textContent);
    let activeRun = null;
    const say = (key, name = '') => { status.textContent = messages[key].replace('{name}', name); };
    const checkRun = (signal) => {
      if (signal.aborted) throw new DOMException('Animation restarted', 'AbortError');
    };
    const animate = (element, frames, options, signal) => {
      checkRun(signal);
      const animation = element.animate(frames, options);
      const cancel = () => animation.cancel();
      const cleanup = () => signal.removeEventListener('abort', cancel);
      signal.addEventListener('abort', cancel, { once: true });
      animation.finished.then(cleanup, cleanup);
      return animation;
    };
    const highlightProduct = (kind, signal) => {
      const product = station.querySelector('[data-tsl-product="' + kind + '"]');
      if (REDUCED_MOTION.matches) return;
      animate(product, [{ boxShadow: '0 0 0 0 #ff625a00' }, { boxShadow: '0 0 30px 3px #ff625a88', borderColor: '#ff625a', offset: .45 }, { boxShadow: '0 0 0 0 #ff625a00' }], { duration: 1400, easing: 'ease-in-out' }, signal);
    };
    const transferCard = async (signal) => {
      const workspace = station.querySelector('.tsl__sleeve-workspace');
      const source = soft.querySelector('.tsl__soft-pocket');
      const destination = loader.querySelector('.tsl__sleeved-insert');
      if (REDUCED_MOTION.matches) {
        soft.classList.add('is-transferred');
        loader.classList.add('is-received');
        return;
      }
      const origin = workspace.getBoundingClientRect();
      const from = source.getBoundingClientRect();
      loader.classList.add('is-measuring');
      const to = destination.getBoundingClientRect();
      loader.classList.remove('is-measuring');
      const flying = document.createElement('div');
      flying.className = 'tsl__transfer-card';
      flying.setAttribute('aria-hidden', 'true');
      const image = document.createElement('img');
      image.src = source.querySelector('img').src;
      image.alt = '';
      flying.append(image);
      Object.assign(flying.style, { left: (from.left - origin.left) + 'px', top: (from.top - origin.top) + 'px', width: from.width + 'px', height: from.height + 'px' });
      workspace.append(flying);
      soft.classList.add('is-transferred');
      const dx = to.left - from.left;
      const dy = to.top - from.top;
      const scale = ' scale(' + to.width / from.width + ',' + to.height / from.height + ')';
      try {
        await animate(flying, [
          { transform: 'translate(0,0)' },
          { transform: 'translate(' + dx * .4 + 'px,' + Math.min(-55, dy * .2) + 'px) rotate(-5deg)', offset: .3 },
          { transform: 'translate(' + dx + 'px,' + (dy - to.height * .6) + 'px)' + scale, offset: .65 },
          { transform: 'translate(' + dx + 'px,' + dy + 'px)' + scale }
        ], { duration: 1900, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards' }, signal).finished;
        checkRun(signal);
        loader.classList.add('is-received');
      } finally { flying.remove(); }
    };
    const reset = (pull) => {
      pull.classList.remove('is-ripping', 'is-revealed', 'is-resetting');
      pull.setAttribute('aria-pressed', 'false');
      pull.setAttribute('aria-label', pull.dataset.packLabel);
    };
    const resetStages = () => {
      station.querySelectorAll('.tsl__transfer-card').forEach(card => card.remove());
      [soft, loader].forEach(stage => stage.classList.remove('is-resetting', 'is-extracting', 'is-opening-sleeve', 'is-inserting', 'is-protected', 'is-transferred', 'is-received', 'is-measuring'));
    };
    const resetPulls = async (items, signal) => {
      const revealed = items.filter(item => item.classList.contains('is-revealed'));
      revealed.forEach(item => item.classList.add('is-resetting'));
      if (revealed.length && !REDUCED_MOTION.matches) await wait(300, signal);
      items.forEach(reset);
      if (revealed.length && !REDUCED_MOTION.matches) await wait(60, signal);
      revealed.forEach(item => item.classList.remove('is-resetting'));
      if (revealed.length && !REDUCED_MOTION.matches) await wait(400, signal);
    };
    pulls.forEach((pull) => {
      pull.dataset.packLabel = pull.getAttribute('aria-label');
      pull.setAttribute('aria-pressed', 'false');
      pull.addEventListener('click', async () => {
        activeRun?.abort();
        const controller = new AbortController();
        const { signal } = controller;
        activeRun = controller;
        pulls.forEach(reset);
        resetStages();
        void station.offsetWidth;
        pull.classList.add('is-ripping');
        pull.setAttribute('aria-pressed', 'true');
        say('opening');
        try {
          const name = await revealExampleCard(pull, pull.querySelector('.tsl__pull-hit img'));
          checkRun(signal);
          if (!REDUCED_MOTION.matches) await wait(720, signal);
          pull.classList.add('is-revealed');
          say('revealed', name);
          if (!REDUCED_MOTION.matches) await wait(850, signal);
          cards.forEach(card => { card.src = pull.dataset.cardSrc; });
          soft.classList.remove('is-extracting', 'is-opening-sleeve', 'is-inserting', 'is-protected');
          loader.classList.remove('is-extracting', 'is-inserting', 'is-protected');
          void soft.offsetWidth;
          if (window.innerWidth <= 1000) soft.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'instant' : 'smooth', block: 'center' });
          soft.classList.add('is-extracting');
          say('extracting');
          if (!REDUCED_MOTION.matches) await wait(1150, signal);
          soft.classList.add('is-opening-sleeve');
          say('opening_sleeve');
          if (!REDUCED_MOTION.matches) await wait(650, signal);
          highlightProduct('sleeve', signal);
          soft.classList.add('is-inserting');
          say('sleeving', name);
          if (!REDUCED_MOTION.matches) await wait(1500, signal);
          soft.classList.add('is-protected');
          if (window.innerWidth <= 600) loader.scrollIntoView({ behavior: REDUCED_MOTION.matches ? 'instant' : 'smooth', block: 'center' });
          loader.classList.add('is-extracting');
          say('extracting_loader');
          if (!REDUCED_MOTION.matches) await wait(1150, signal);
          say('loading');
          highlightProduct('loader', signal);
          await transferCard(signal);
          checkRun(signal);
          loader.classList.add('is-protected');
          say('complete', name);
          await resetPulls([pull], signal);
        } catch (error) {
          if (!signal.aborted) throw error;
        } finally {
          if (activeRun === controller) activeRun = null;
        }
      });
    });
  };

  const initialize = (root) => {
    if (!root || root.dataset.tslInitialized === 'true') return;
    root.dataset.tslInitialized = 'true';
    initializeScene(root);
    initializeSleeveStation(root);
    initializeReveals(root);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll('[data-tsl]').forEach(initialize);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
